import {
  collection,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  getDocs,
  query,
  orderBy,
  Timestamp,
  writeBatch,
  where,
} from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import { db } from './config';
import { WeeklyDeal, Product } from '../types';

const WEEKLY_DEALS_COLLECTION = 'weekly_deals';
const PRODUCTS_COLLECTION = 'products';

const checkAuth = () => {
  const auth = getAuth();
  if (!auth.currentUser) {
    throw new Error('Missing or insufficient permissions.');
  }
};

export const addWeeklyDeal = async (dealData: Omit<WeeklyDeal, 'id' | 'createdAt' | 'updatedAt'>) => {
  try {
    checkAuth();
    const docRef = await addDoc(collection(db, WEEKLY_DEALS_COLLECTION), {
      ...dealData,
      createdAt: Timestamp.now(),
      updatedAt: Timestamp.now(),
    });
    return docRef.id;
  } catch (error) {
    console.error('Error adding weekly deal:', error);
    throw error;
  }
};

export const updateWeeklyDeal = async (id: string, dealData: Partial<WeeklyDeal>) => {
  try {
    checkAuth();
    const dealRef = doc(db, WEEKLY_DEALS_COLLECTION, id);
    await updateDoc(dealRef, {
      ...dealData,
      updatedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error updating weekly deal:', error);
    throw error;
  }
};

export const deleteWeeklyDeal = async (id: string) => {
  try {
    checkAuth();
    const batch = writeBatch(db);
    
    // Remove deal reference from all products
    const productsSnapshot = await getDocs(collection(db, PRODUCTS_COLLECTION));
    productsSnapshot.docs.forEach(productDoc => {
      const productData = productDoc.data();
      if (productData.weeklyDealId === id) {
        batch.update(productDoc.ref, {
          weeklyDealId: null,
          discountPercentage: null,
        });
      }
    });

    // Delete the deal
    const dealRef = doc(db, WEEKLY_DEALS_COLLECTION, id);
    batch.delete(dealRef);

    await batch.commit();
  } catch (error) {
    console.error('Error deleting weekly deal:', error);
    throw error;
  }
};

export const getWeeklyDeals = async (): Promise<WeeklyDeal[]> => {
  try {
    // Query only active deals
    const dealsQuery = query(
      collection(db, WEEKLY_DEALS_COLLECTION),
      where('active', '==', true),
      orderBy('createdAt', 'desc')
    );
    
    const [dealsSnapshot, productsSnapshot] = await Promise.all([
      getDocs(dealsQuery),
      getDocs(collection(db, PRODUCTS_COLLECTION))
    ]);
    
    const products = productsSnapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt.toDate(),
    })) as Product[];

    return dealsSnapshot.docs.map(doc => {
      const deal = {
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt.toDate(),
        updatedAt: doc.data().updatedAt.toDate(),
      } as WeeklyDeal;

      deal.products = products.filter(p => p.weeklyDealId === deal.id);
      return deal;
    });
  } catch (error) {
    console.error('Error getting weekly deals:', error);
    throw error;
  }
};

export const addProductToWeeklyDeal = async (productId: string, dealId: string, discountPercentage: number) => {
  try {
    checkAuth();
    const productRef = doc(db, PRODUCTS_COLLECTION, productId);
    await updateDoc(productRef, {
      weeklyDealId: dealId,
      discountPercentage,
    });
  } catch (error) {
    console.error('Error adding product to weekly deal:', error);
    throw error;
  }
};

export const removeProductFromWeeklyDeal = async (productId: string) => {
  try {
    checkAuth();
    const productRef = doc(db, PRODUCTS_COLLECTION, productId);
    await updateDoc(productRef, {
      weeklyDealId: null,
      discountPercentage: null,
    });
  } catch (error) {
    console.error('Error removing product from weekly deal:', error);
    throw error;
  }
};